<template>
  <basic-dialog v-model="dialog" :actionButtons="actionButtons" :loading="saving"
                :title="$t('surveys.share_with_email')">

    <v-card-text v-if="!loading" class="scroll-content">
      <v-alert :value="isSent" border="left" class="mt-4"
               color="warning"
               colored-border dense
               elevation="4"
               icon="mdi mdi-exclamation" prominent>
        {{ $t('surveys.sent_help_text', [$formatDateTime(surveyShare.sent)]) }}
      </v-alert>
      <div :class="$vuetify.breakpoint.xsOnly ? 'caption' : ''" class="mt-4 mb-6">
        {{ $t('required_fields_are_marked_with_asterisk') }} (<span class="red--text">*</span>).
      </div>
      <v-form ref="form">
        <v-select v-model="selectedLanguage"
                  :dense="$vuetify.breakpoint.xsOnly"
                  :items="languages"
                  :label="$t('surveys.share_email_language')"
                  :rules="[$rules.required]"
                  class="required"
                  outlined></v-select>

        <v-select :items="[1]"
                  :label="$t('surveys.sharing_survey_to')"
                  :value="1"
                  append-icon=""
                  outlined
                  readonly>
          <template v-slot:selection>
            <div :style="{color: $randomizeColorFromString(teamNames)}" class="font-weight-bold">
              {{ teamNames }}
            </div>
          </template>
        </v-select>
        <div
          v-if="!$isPaidTenant || ($isPaidTenant && team.answerers && team.answerers.length >= $planLimit.answerers - 10)"
          class="mt-2 mb-5 d-flex">
          <!-- Will be shown if tenant is not paid or if its paid but is 10 recipients away from limit-->
          <div class="mr-2">
            <plan-chip/>
          </div>
          <div class="flex-grow-1">
            {{ $t('teams.max_recipients_x', [$planLimit.answerers, $nextPlan.name]) }}
          </div>
        </div>
        <v-text-field v-model="recipients"
                      :dense="$vuetify.breakpoint.xsOnly"
                      :hint="$t('teams.add_recipients_hint')"
                      :label="$t('teams.add_email_to_recipients')"
                      outlined
                      persistent-hint
                      @keypress.enter="addRecipients">
          <template v-slot:append-outer>
            <v-btn :large="$vuetify.breakpoint.smAndUp"
                   class="ma-0 recipients-button"
                   color="accent"
                   @click="addRecipients">
              {{ $t('add') }}
            </v-btn>
          </template>
        </v-text-field>
        <v-alert v-if="notValidEmails.length > 0" class="mt-2 px-3 caption" color="error" dense text>
          <div class="font-weight-bold mb-1">{{ $t('teams.invalid_email_addresses') }}</div>
          <template v-for="email in notValidEmails">
            <div :key="email">{{ email }}</div>
          </template>
        </v-alert>
        <v-alert v-if="selectedRecipients && selectedRecipients.length > 0" class="mt-2 px-3" color="grey lighten-4"
                 dense>
          <div class="caption grey--text text--darken-1">
            <div v-if="areAllRecipientsDeselected" @click.stop="selectAllRecipients">
              <v-btn color="grey darken-1" icon small style="margin-top: -2px;" @click.stop="selectAllRecipients">
                <v-icon>mdi mdi-checkbox-blank-outline</v-icon>
              </v-btn>
              {{ $t('select_all') }}
            </div>
            <div v-else-if="areAllRecipientsSelected" @click.stop="deselectAllRecipients">
              <v-btn color="grey darken-1" icon small style="margin-top: -2px;" @click.stop="deselectAllRecipients">
                <v-icon>mdi mdi-checkbox-outline</v-icon>
              </v-btn>
              {{ $t('deselect_all') }}
            </div>
            <div v-else @click.stop="selectAllRecipients">
              <v-btn color="grey darken-1" icon small style="margin-top: -2px;" @click.stop="selectAllRecipients">
                <v-icon>mdi mdi-checkbox-intermediate</v-icon>
              </v-btn>
              {{ $t('select_all') }}
            </div>
          </div>
          <v-divider class="my-2"></v-divider>
          <div v-for="recipient in selectedRecipients" :key="recipient.email" @click="toggleRecipient(recipient)">
            <v-btn icon small style="margin-top: -2px;" @click.stop="toggleRecipient(recipient)">
              <v-icon v-if="recipient.selected">mdi mdi-checkbox-outline</v-icon>
              <v-icon v-else>mdi mdi-checkbox-blank-outline</v-icon>
            </v-btn>
            {{ recipient.email }}
          </div>
        </v-alert>
      </v-form>
    </v-card-text>

    <v-card-text v-else class="py-10">
      <hb-loading-indicator align-middle/>
    </v-card-text>

  </basic-dialog>
</template>

<script>
import BasicDialog from "@/components/BasicDialog";
import TranslationService from "@/service/translation.service";
import surveyShareApi from "@/api/surveyshare";
import PlanChip from "@/components/Tenant/Reports/PlanChip";

export default {
  name: "ShareWithEmailDialog",
  components: {PlanChip, BasicDialog},
  data() {
    return {
      loading: true,
      saving: false,
      dialog: false,
      team: {},
      surveyShare: {},
      selectedLanguage: null,
      recipients: '',
      selectedRecipients: [],
      actionButtons: {
        cancel: {
          click: this.close,
        },
        save: {
          click: this.send,
          text: this.$t('send'),
          icon: 'mdi mdi-send',
        }
      },
      notValidEmails: [],
    };
  },
  methods: {
    deselectAllRecipients() {
      this.selectedRecipients.forEach(r => r.selected = false);
    },
    selectAllRecipients() {
      this.selectedRecipients.forEach(r => r.selected = true);
    },
    toggleRecipient(recipient) {
      const rIndex = this.selectedRecipients.findIndex(r => r.id === recipient.id)
      if (rIndex > -1) {
        this.selectedRecipients[rIndex].selected = !this.selectedRecipients[rIndex].selected;
      }
    },
    addRecipients() {
      if (this.recipients.length === 0) return;
      this.notValidEmails = [];
      let newRecipients = this.recipients.split(';');
      if (newRecipients.length > 0) {
        const notValidEmails = newRecipients.map(r => r.trim()).filter(r => this.$rules.email(r) !== true);
        if (notValidEmails) {
          this.notValidEmails = notValidEmails;
        }
        newRecipients = newRecipients.map(r => r.trim()).filter(r => this.$rules.email(r) === true);
        let overLimitFound = false;
        newRecipients.forEach((newRecipient) => {
          if (this.selectedRecipients.findIndex(a => a.email === newRecipient) === -1) {
            if (this.selectedRecipients.length >= this.$planLimit.answerers) {
              overLimitFound = true;
            } else {
              this.selectedRecipients.push({email: newRecipient, selected: true});
            }
          }
        })
        if (overLimitFound) {
          this.$showPlanNotification(this.$t('teams.max_recipients_reached_warning', [this.$nextPlan.name]));
        }
      }
      this.recipients = '';
    },
    close() {
      this.dialog = false;
    },
    async open(surveyShare) {
      this.loading = true;
      this.team = {
        type: null,
        name: '',
        description: '',
        answerers: []
      };
      this.recipients = '';
      this.dialog = true;

      if (surveyShare) {
        this.surveyShare = surveyShare;
        this.selectRecipients()
      }
      this.loading = false;
    },
    async selectRecipients() {
      this.selectedRecipients = [...this.allAnswerers.map(t => {
        return {
          ...t,
          selected: true
        }
      })];
    },
    async send() {
      if (!this.$refs.form) return;
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      try {
        await surveyShareApi.sendSurveyShare(this.surveyShare, this.selectedRecipients.filter(s => s.selected).map(e => e.email), this.selectedLanguage);
        this.dialog = false;
      } catch (e) {
        this.$handleApiError(e, this.$t('surveys.errors.error_sharing_survey'));
        console.error(e);
      }
      this.$emit('refresh');
      this.saving = false;
    }
  },
  computed: {
    isSent() {
      return !!this.surveyShare.sent;
    },
    teamNames() {
      return this.surveyShare?.teams?.map(t => t.name).join(', ');
    },
    areAllRecipientsSelected() {
      return this.selectedRecipients.filter(r => r.selected === false).length === 0;
    },
    areAllRecipientsDeselected() {
      return this.selectedRecipients.filter(r => r.selected === true).length === 0;
    },
    languages() {
      return TranslationService.languages.map(l => {
        return {
          text: this.$t('translations.languages.' + l),
          value: l,
        };
      });
    },
    allAnswerers() {
      return this.surveyShare?.teams?.flatMap(t => t.answerers)
    },
  },
  watch: {
    dialog(val) {
      if (!val && this.$refs.form) {
        this.selectedLanguage = null;
        this.$refs.form.resetValidation();
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.scroll-content {
  max-height: calc(100vh - 240px);
  overflow: auto;
}

@media screen and (min-width: 600px) {
  .scroll-content {
    max-height: calc(100vh - 300px);
    overflow: auto;
  }
}

.recipients-button {
  margin-top: -18px !important;
}

@media screen and (max-width: 599px) {
  .recipients-button {
    margin-top: -8px !important;
    height: 40px !important;
  }
}
</style>
